/**
 * Strapi response/error unwrapping.
 *
 * Replaces the ~15 ad-hoc `res.data?.data ?? []` sites and the unsafe
 * `err.response.data.error.details.errors` drills scattered across the app with
 * one typed seam.
 */
import type { AxiosResponse } from "axios";
import type { StrapiListResponse, StrapiResponse } from "../types/api";
import { MSG } from "./messages";

/** Unwrap a single-entity response: `res.data.data`. */
export function unwrap<T>(res: AxiosResponse<StrapiResponse<T>>): T {
  return res.data.data;
}

/**
 * Unwrap a collection response to a plain array. Tolerates the
 * users-permissions shape, which returns a bare array rather than `{ data: [] }`.
 */
export function unwrapList<T>(res: AxiosResponse<StrapiListResponse<T> | T[]>): T[] {
  const body = res.data;
  if (Array.isArray(body)) return body;
  return body?.data ?? [];
}

/** A single Strapi field-validation error (as surfaced under `details.errors`). */
export interface StrapiFieldError {
  path?: Array<string | number>;
  message?: string;
  name?: string;
}

interface ErrorLike {
  response?: {
    data?: {
      // Strapi shape (`data.error.*`) + the flatter shape some non-Strapi
      // endpoints (e.g. the WordPress bridge) return (`data.message`).
      message?: string;
      error?: {
        message?: string;
        details?: { errors?: StrapiFieldError[] };
      };
    };
  };
  message?: string;
}

/**
 * Safe extraction of a human-readable message from a Strapi/axios error,
 * preferring the most specific detail available.
 */
export function getErrorMessage(err: unknown): string {
  const e = err as ErrorLike;
  return (
    e?.response?.data?.error?.details?.errors?.[0]?.message ??
    e?.response?.data?.error?.message ??
    e?.response?.data?.message ??
    e?.message ??
    MSG.common.error
  );
}

/**
 * Safe extraction of the per-field validation errors array a form binds to
 * (`errorHandling`/`formErrorHandling`), replacing the unguarded
 * `err.response.data.error.details.errors` drills that throw inside a catch on
 * any non-Strapi (network/timeout/JS) error. Returns `[]` when absent.
 */
export function getFieldErrors(err: unknown): StrapiFieldError[] {
  const e = err as ErrorLike;
  return e?.response?.data?.error?.details?.errors ?? [];
}

/**
 * Look up the validation message for a single form field within a
 * `formErrorHandling`/`errorHandling` array (the shape `getFieldErrors`
 * returns). Safe against entries whose `path` is absent — a network/JS error in
 * the array would otherwise throw on `e.path[0]` during render. Returns
 * `undefined` when the field has no error.
 */
export function fieldError(
  errors: StrapiFieldError[] | undefined | null,
  field: string
): string | undefined {
  return errors?.find((e) => e.path?.[0] === field)?.message;
}
